import { useCallback, useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/electron/renderer'

const SAVE_DELAY_MS = 800
const SAVED_VISIBLE_MS = 1_500

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error'

interface DebouncedAutosave {
  status: AutosaveStatus
  /** Queues `value` to be persisted once edits pause. */
  schedule: (value: string) => void
  flush: () => void
  /** Records `value` as already persisted, so scheduling it again is a no-op. */
  markSaved: (value: string) => void
}

/**
 * Debounced save for a free-text field. Pending edits flush on unmount, and a
 * failed save re-queues its value so the next flush retries it.
 */
export function useDebouncedAutosave(persist: (value: string) => Promise<unknown>): DebouncedAutosave {
  const [status, setStatus] = useState<AutosaveStatus>('idle')
  const lastSavedRef = useRef<string | null>(null)
  const pendingRef = useRef<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const statusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isSavingRef = useRef(false)
  const isMountedRef = useRef(true)

  // Saves run one at a time so an older request can never land after a newer one.
  const drainPending = useCallback(async () => {
    isSavingRef.current = true
    let outcome: AutosaveStatus | null = null
    while (pendingRef.current !== null && pendingRef.current !== lastSavedRef.current) {
      const next = pendingRef.current
      pendingRef.current = null
      if (statusTimerRef.current) {
        clearTimeout(statusTimerRef.current)
        statusTimerRef.current = null
      }
      if (isMountedRef.current) {
        setStatus('saving')
      }
      try {
        await persist(next)
        lastSavedRef.current = next
        outcome = 'saved'
      } catch (error) {
        Sentry.captureException(error)
        outcome = 'error'
        if (pendingRef.current === null) {
          pendingRef.current = next
          break
        }
      }
    }
    if (pendingRef.current === lastSavedRef.current) {
      pendingRef.current = null
    }
    isSavingRef.current = false
    if (!isMountedRef.current || outcome === null) {
      return
    }
    setStatus(outcome)
    if (outcome === 'saved') {
      statusTimerRef.current = setTimeout(() => setStatus('idle'), SAVED_VISIBLE_MS)
    }
  }, [persist])

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (isSavingRef.current) {
      return
    }
    void drainPending()
  }, [drainPending])

  const schedule = useCallback(
    (value: string) => {
      pendingRef.current = value
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      timerRef.current = setTimeout(flush, SAVE_DELAY_MS)
    },
    [flush],
  )

  const markSaved = useCallback((value: string) => {
    lastSavedRef.current = value
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if (statusTimerRef.current) {
        clearTimeout(statusTimerRef.current)
      }
      flush()
    }
  }, [flush])

  return { status, schedule, flush, markSaved }
}
